import { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Carrot } from "@/components/Carrot";
import { CarrotIcon } from "@/components/CarrotIcon";
// The policy text is the single source of truth — imported raw and rendered.
import policy from "@/docs/CarrotOnTheHorse_Privacy_Policy.md" with { type: "text" };

/** Tailwind-styled renderers so the markdown reads in the site's own voice. */
const components = {
  h1: ({ children }: { children?: React.ReactNode }) => (
    <h1 className="text-balance text-4xl font-extrabold leading-[1.05] text-ink sm:text-5xl">{children}</h1>
  ),
  h2: ({ children }: { children?: React.ReactNode }) => (
    <h2 className="mt-14 scroll-mt-24 text-2xl font-bold tracking-tight text-ink">{children}</h2>
  ),
  p: ({ children }: { children?: React.ReactNode }) => (
    <p className="mt-4 leading-relaxed text-ink/75">{children}</p>
  ),
  ul: ({ children }: { children?: React.ReactNode }) => (
    <ul className="mt-4 space-y-2 text-ink/75">{children}</ul>
  ),
  li: ({ children }: { children?: React.ReactNode }) => (
    <li className="relative pl-6 leading-relaxed before:absolute before:left-0 before:top-[0.6em] before:size-1.5 before:rounded-full before:bg-carrot">
      {children}
    </li>
  ),
  strong: ({ children }: { children?: React.ReactNode }) => (
    <strong className="font-semibold text-ink">{children}</strong>
  ),
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a
      href={href}
      className="font-medium text-carrot-deep underline decoration-carrot/40 decoration-2 underline-offset-2 transition-colors hover:decoration-carrot"
    >
      {children}
    </a>
  ),
  hr: () => <hr className="mt-12 border-0 border-t border-ink/10" />,
} as const;

export function PrivacyPolicy() {
  useEffect(() => {
    document.title = "Privacy Policy | CarrotOnTheHorse";
  }, []);

  return (
    <div className="min-h-screen">
      <header className="border-b border-ink/10 bg-cream/80 backdrop-blur-md">
        <nav className="mx-auto flex max-w-3xl items-center justify-between gap-3 px-5 py-3.5 sm:px-8">
          <a href="/" className="group flex min-w-0 items-center gap-2.5" aria-label="CarrotOnTheHorse home">
            <Carrot className="h-6 w-6 shrink-0 transition-transform duration-300 group-hover:-rotate-12 sm:h-7 sm:w-7" />
            <span className="truncate font-display text-base font-bold tracking-tight text-ink sm:text-lg">
              Carrot<span className="text-carrot">OnThe</span>Horse
            </span>
          </a>
          <a
            href="/"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-ink/15 bg-paper px-3 py-2 text-sm font-semibold text-ink transition-all hover:border-carrot hover:text-carrot-deep sm:px-4"
          >
            <CarrotIcon direction="left" className="carrot-icon-brand h-[1.125rem] w-3.5" />
            <span className="hidden sm:inline">Back to site</span>
          </a>
        </nav>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-14 sm:px-8 sm:py-20">
        <article>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
            {policy}
          </ReactMarkdown>
        </article>
      </main>

      <footer className="border-t border-ink/10 bg-paper/60 py-6">
        <p className="text-center text-xs text-ink/55">
          © 2026 Carrot On The Horse, Inc. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export default PrivacyPolicy;
